import { useDeleteBlog, useGetSingleBlog } from '@app/Hooks/use-blogs';
import React, { useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit3,
  Clock,
  Calendar,
  User,
  Tag,
  Trash2,
  Loader2
} from 'lucide-react';
import { renderMarkdown } from './new-story-helper';

function formatDate(dateStr) {
  if (!dateStr) return null;
  try {
    return new Date(dateStr)?.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return null;
  }
}

const STATUS_CONFIG = {
  published: { label: 'Live', dot: 'bg-emerald-400', text: 'text-emerald-400' },
  draft: { label: 'Draft', dot: 'bg-zinc-400', text: 'text-zinc-400' },
  archived: { label: 'Archived', dot: 'bg-rose-400', text: 'text-rose-400' }
};

const ReadStory = () => {
  const { id } = useParams();
  const contentRef = useRef(null);
  const { data, isLoading } = useGetSingleBlog(id);
  const displayDate = formatDate(data?.publish_date || data?.created_at);
  const words = data?.content.trim().split(/\s+/).filter(Boolean).length;
  const statusCfg = STATUS_CONFIG[data?.status || 'draft'] || STATUS_CONFIG.draft;
  const { mutateAsync, isPending } = useDeleteBlog();

  if (isLoading) {
    return (
      <div className="grid place-items-center h-screen">
        <Loader2 className="animate-spin" color="#003232" />
      </div>
    );
  }

  const handleDelete = async () => {
    await mutateAsync(id);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] relative">
      <nav className="sticky bg-[#FAFAF7]/80 border-b border-[#E8E8E0]/80 top-0 z-50 backdrop-blur-[12px] border">
        <div className="flex justify-between items-center px-6 mx-auto max-w-4xl h-14">
          <Link
            to="/blogs"
            className="flex items-center gap-2 text-[#777770] hover:text-[#1A1A1A] text-sm transition-colors"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">All posts</span>
          </Link>
          <div className="flex gap-1.5 items-center">
            {/* Status */}
            <span
              className={`hidden sm:flex items-center gap-1.5 text-[11px] font-medium ${statusCfg.text} mr-1`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
              {statusCfg.label}
            </span>

            <Link
              to={`/edit-story/${id}`}
              className="flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium text-white bg-[#003232] rounded-lg transition-all"
            >
              <Edit3 size={13} />
              Edit
            </Link>
            <button
              onClick={handleDelete}
              className="flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium text-white bg-red-500 rounded-lg transition-all disabled:opacity-50"
              disabled={isPending}
            >
              {isPending ? (
                'Deleting...'
              ) : (
                <>
                  {' '}
                  <Trash2 size={13} />
                  Delete
                </>
              )}
            </button>
          </div>
        </div>
      </nav>

      {data?.featured_image && (
        <div className="w-full max-h-[520px] overflow-hidden">
          <img
            src={data?.featured_image}
            alt={data?.title}
            className="object-cover w-full h-full"
          />
        </div>
      )}

      <article className="px-6 pb-24 mx-auto max-w-4xl">
        {/* Header */}
        <header className="pt-14 pb-10 border-b border-[#E8E8E0]">
          {/* Title */}
          <h1 className="bp-serif text-4xl sm:text-5xl font-light text-[#1A1A1A] leading-tight mb-5">
            {data?.title}
          </h1>

          {/* Excerpt / subtitle */}
          {data?.excerpt && (
            <p className="bp-serif text-xl text-[#666650] font-light italic leading-relaxed mb-7">
              {data?.excerpt}
            </p>
          )}

          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[#999990] text-sm">
            {data?.author && (
              <span className="flex gap-1.5 items-center">
                <span className="w-7 h-7 rounded-full bg-[#1A1A1A] flex items-center justify-center">
                  <User size={13} className="text-white/70" />
                </span>
                {data?.author}
              </span>
            )}
            {displayDate && (
              <span className="flex gap-1.5 items-center">
                <Calendar size={13} />
                {displayDate}
              </span>
            )}
            <span className="flex gap-1.5 items-center">
              <Clock size={13} />
              {data?.reading_time} min read
              <span className="text-[#CCC8C0]">·</span>
              {words?.toLocaleString()} words
            </span>
          </div>
        </header>

        {/* Body */}
        <div
          ref={contentRef}
          className="pt-10 bp-prose"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(data?.content) }}
        />

        {/* Footer — tags */}
        {data?.tags?.length > 0 && (
          <footer className="mt-16 pt-8 border-t border-[#E8E8E0]">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-[#BBBB B0] mb-3">
              Tags
            </p>
            <div className="flex flex-wrap gap-2">
              {data?.tags?.map((tag, i) => (
                <span
                  key={i}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#666660] bg-[#F0F0E8] border border-[#E0E0D8] rounded-full hover:bg-[#E8E8E0] transition-colors cursor-default"
                >
                  <Tag size={11} />
                  {tag}
                </span>
              ))}
            </div>
          </footer>
        )}

        {/* Back CTA */}
        <div className="mt-16 pt-8 border-t border-[#E8E8E0] flex justify-center">
          <Link
            to="/blogs"
            className="flex items-center gap-2 px-5 py-2.5 text-sm text-[#777770] hover:text-[#1A1A1A] bg-[#F0F0E8] hover:bg-[#E8E8E0] border border-[#E0E0D8] rounded-xl transition-all"
          >
            <ArrowLeft size={15} />
            Back to all posts
          </Link>
        </div>
      </article>
    </div>
  );
};

export default ReadStory;
